/**
 * My profile in this workspace: photo (crop → 512 px WebP → upload → attach), names, title, pronouns, phone (private
 * unless shared), time zone (auto-detected, searchable), language (also the app's and my emails'), and status with
 * "clear after".
 *
 * The form is a draft saved with "Save"; status, photo and language apply at once. Saves are optimistic: the profile
 * shows the new values right away; offline they wait and are sent when the network is back (the server dedupes on the
 * Idempotency-Key); a refusal rolls back with a notice. Other clients see changes live (`member.updated`).
 */
import type { Locale, Member, MemberAvatar, UpdateProfileRequest, UserStatus } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { handleProblem, handleProblemMessage, normalizeHandle } from "../logic/handles.ts";
import {
  activeStatus,
  applyProfilePatch,
  avatarLook,
  memberAvatar,
  draftFromMember,
  formatClock,
  formatOffset,
  hasProblems,
  isEmptyPatch,
  localTimeParts,
  missingDefaults,
  profilePatch,
  profileProblems,
  searchZones,
  statusExpiresAt,
  statusProblem,
  zoneCity,
  zoneRegion,
  type AvatarLook,
  type ProfileDraft,
  type ProfileProblems,
  type StatusDuration,
} from "../logic/profile.ts";
import { errorKind, type CropArea, type PickedFile } from "../ports.ts";
import { IDLE_PHOTO, PhotoPipeline, type PhotoState, type SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const ZONE_RESULTS = 50;
/** Wait this long after typing before checking a username. */
export const HANDLE_CHECK_DELAY_MS = 350;

/** The avatar picker (non-photo styles; photos go through `pickPhoto`). */
export type AvatarEditor = { kind: "initials" | "emoji"; color: number; emoji: string };

/** One row of the time zone picker. */
export type ZoneOption = { zone: string; city: string; region: string; offset: string };

export type ProfileSettingsState = {
  status: "loading" | "ready" | "error";
  /** My profile as saved (with the optimistic overlay while a save is in flight or queued). */
  member?: Member;
  email?: string;
  draft: ProfileDraft;
  problems: ProfileProblems;
  /** The form differs from the saved profile. */
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  /** A save is waiting for the network. */
  queued: boolean;
  /** What the avatar shows: the new picture while it uploads, else the saved photo. */
  avatarUrl?: string;
  /** Initials / emoji tone of the saved avatar (the Avatar primitive's `tone` / `emoji`). */
  look: AvatarLook;
  avatarEditor?: AvatarEditor;
  /** Username check: idle (unchanged), checking, available, taken (with suggestions) or invalid (see problems). */
  handleStatus: "idle" | "checking" | "available" | "taken" | "invalid";
  handleSuggestions: string[];
  photo: PhotoState;
  /** Time zone picker. */
  zoneQuery: string;
  zones: ZoneOption[];
  deviceZone: string;
  /** "3:05 PM" in the draft's zone. */
  localTime?: string;
  /** Status editor. */
  statusEmoji: string;
  statusText: string;
  statusDuration: StatusDuration;
  statusProblem?: MessageDescriptor;
  /** The status others see now (unexpired). */
  currentStatus?: UserStatus;
  notice?: MessageDescriptor;
};

const EMPTY_DRAFT: ProfileDraft = { handle: "", displayName: "", fullName: "", title: "", pronouns: "", phone: "", phoneVisible: false, timezone: "", locale: "en" };

export class ProfileSettingsViewModel extends ViewModel<ProfileSettingsState> {
  readonly ws: WorkspaceViewModel;
  private readonly deps: SettingsDeps;
  private readonly appLocale: () => Locale;
  private readonly photos: PhotoPipeline;
  /** The saved profile (server truth), the base of the draft comparison. */
  private saved: Member | undefined;
  /** Writes not confirmed yet, in order (replayed when the network is back). */
  private queue: { req: UpdateProfileRequest; clientId: string }[] = [];
  private flushing = false;
  private allZones: string[] | undefined;
  private cancelHandleCheck: (() => void) | undefined;
  private handleGeneration = 0;
  private takenHandles = new Set<string>();

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps, appLocale: () => Locale) {
    super({
      status: "loading",
      draft: EMPTY_DRAFT,
      problems: {},
      dirty: false,
      canSave: false,
      saving: false,
      queued: false,
      photo: IDLE_PHOTO,
      look: {},
      handleStatus: "idle",
      handleSuggestions: [],
      zoneQuery: "",
      zones: [],
      deviceZone: deps.zones.device(),
      statusEmoji: "",
      statusText: "",
      statusDuration: "today",
    });
    this.ws = ws;
    this.deps = deps;
    this.appLocale = appLocale;
    this.photos = new PhotoPipeline(ws, deps.images, { type: "avatar" }, (fileId) => this.attachPhoto(fileId), (photo) => this.update({ photo, ...this.avatar(photo) }));
    this.onDispose(() => this.photos.dispose());
    this.onDispose(() => this.cancelHandleCheck?.());
    let online = ws.state.network;
    this.onDispose(
      ws.subscribe(() => {
        const now = ws.state.network;
        if (now && !online) void this.flush();
        online = now;
        this.followCache();
      }),
    );
  }

  private get wsId(): string {
    return this.ws.wsId;
  }

  private zoneList(): string[] {
    this.allZones ??= this.deps.zones.list();
    return this.allZones;
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  private avatar(photo: PhotoState): { avatarUrl?: string } {
    if (photo.phase === "uploading" && photo.preview !== undefined) return { avatarUrl: photo.preview };
    const url = this.state.member?.avatarUrl;
    return url === undefined ? { avatarUrl: undefined } : { avatarUrl: url };
  }

  // ---------- Loading ----------

  /** Fetches my profile (each time settings open); offline, the cached member is shown read-only until it loads. */
  async load(): Promise<void> {
    const cached = this.ws.state.cache?.members[this.ws.state.meId];
    if (this.saved === undefined && cached !== undefined) this.adopt(cached, true);
    try {
      const me = await this.deps.api.myProfile(this.wsId);
      if (this.isDisposed) return;
      this.adopt(me, !this.state.dirty);
      const defaults = missingDefaults(me, this.state.deviceZone, this.appLocale());
      if (!isEmptyPatch(defaults)) this.enqueue(defaults);
    } catch (e) {
      if (this.isDisposed) return;
      if (this.saved === undefined) this.update({ status: "error" });
      else if (errorKind(e) !== "network") this.update({ notice: msg("settings.loadFailed") });
    }
  }

  /** A server profile: becomes the saved base; the draft follows unless it's being edited. */
  private adopt(m: Member, resetDraft: boolean): void {
    this.saved = m;
    const shown = this.queue.reduce((acc, w) => applyProfilePatch(acc, w.req), m);
    const draft = resetDraft ? draftFromMember(shown, this.state.deviceZone, this.appLocale()) : this.state.draft;
    const next: Partial<ProfileSettingsState> = { status: "ready", member: shown, draft };
    if (m.email !== undefined) next.email = m.email;
    this.update(next);
    this.revalidate();
  }

  /** Live changes from other devices (member.updated) when nothing is being edited here. */
  private followCache(): void {
    const cached = this.ws.state.cache?.members[this.ws.state.meId];
    const saved = this.saved;
    if (cached === undefined || saved === undefined || this.queue.length > 0) return;
    const merged: Member = { ...cached };
    if (saved.email !== undefined) merged.email = saved.email;
    if (saved.phone !== undefined) merged.phone = saved.phone;
    if (saved.phoneVisible !== undefined) merged.phoneVisible = saved.phoneVisible;
    if (JSON.stringify(merged) === JSON.stringify(saved)) return;
    this.adopt(merged, !this.state.dirty);
  }

  private revalidate(): void {
    const s = this.state;
    const base = this.state.member === undefined ? undefined : draftFromMember(this.state.member, s.deviceZone, this.appLocale());
    const problems = profileProblems(s.draft, this.zoneList());
    const dirty = base !== undefined && !isEmptyPatch(profilePatch(base, s.draft));
    const status = s.member === undefined ? undefined : activeStatus(s.member, this.now());
    const offset = s.draft.timezone === "" ? undefined : this.deps.zones.offsetMinutes(s.draft.timezone, this.now());
    const time = offset === undefined ? undefined : localTimeParts(this.now(), offset);
    const next: ProfileSettingsState = {
      ...s,
      problems,
      dirty,
      canSave: dirty && !hasProblems(problems) && s.member !== undefined && s.handleStatus !== "checking" && s.handleStatus !== "taken",
      look: s.member === undefined ? {} : avatarLook(s.member),
      zones: this.state.zones.length === 0 || this.state.zoneQuery !== "" ? this.zoneOptions(s.zoneQuery) : this.state.zones,
      ...this.avatar(s.photo),
    };
    if (s.member !== undefined && normalizeHandle(s.draft.handle) === (s.member.handle ?? "")) {
      next.handleStatus = "idle";
      next.handleSuggestions = [];
    }
    if (status === undefined) delete next.currentStatus;
    else next.currentStatus = status;
    if (time === undefined) delete next.localTime;
    else next.localTime = formatClock(time.hour, time.minute, this.appLocale());
    this.setState(next);
  }

  private zoneOptions(query: string): ZoneOption[] {
    const now = this.now();
    return searchZones(this.zoneList(), query, ZONE_RESULTS).map((zone) => {
      const offset = this.deps.zones.offsetMinutes(zone, now);
      return { zone, city: zoneCity(zone), region: zoneRegion(zone), offset: offset === undefined ? "" : formatOffset(offset) };
    });
  }

  // ---------- The form ----------

  private edit(patch: Partial<ProfileDraft>): void {
    this.update({ draft: { ...this.state.draft, ...patch } });
    this.revalidate();
  }

  /** Username: validated at once, checked with the server after a pause. */
  setHandle(v: string): void {
    this.edit({ handle: v });
    this.cancelHandleCheck?.();
    this.cancelHandleCheck = undefined;
    const gen = ++this.handleGeneration;
    const handle = normalizeHandle(v);
    const saved = this.state.member?.handle;
    if (handle === "" || handle === saved) {
      this.update({ handleStatus: "idle", handleSuggestions: [] });
      this.revalidate();
      return;
    }
    if (handleProblem(handle) !== undefined) {
      this.update({ handleStatus: "invalid", handleSuggestions: [] });
      this.revalidate();
      return;
    }
    if (this.takenHandles.has(handle)) {
      this.update({ handleStatus: "taken" });
      this.revalidate();
      return;
    }
    this.update({ handleStatus: "checking", handleSuggestions: [] });
    this.revalidate();
    this.cancelHandleCheck = this.ws.deps.clock.setTimer(HANDLE_CHECK_DELAY_MS, () => {
      this.cancelHandleCheck = undefined;
      this.deps.api.checkHandle(this.wsId, handle).then(
        (res) => {
          if (this.isDisposed || gen !== this.handleGeneration) return;
          if (!res.available) this.takenHandles.add(handle);
          this.update({ handleStatus: res.available ? "available" : res.problem === "taken" || res.problem === undefined ? "taken" : "invalid", handleSuggestions: res.suggestions });
          if (res.problem !== undefined && res.problem !== "taken") this.update({ problems: { ...this.state.problems, handle: handleProblemMessage(res.problem) } });
          this.revalidate();
        },
        () => {
          // Offline / server error: let the save decide (the server checks again).
          if (this.isDisposed || gen !== this.handleGeneration) return;
          this.update({ handleStatus: "idle" });
          this.revalidate();
        },
      );
    });
  }

  /** A suggested username. */
  chooseHandleSuggestion(handle: string): void {
    this.setHandle(handle);
  }

  setDisplayName(v: string): void {
    this.edit({ displayName: v });
  }
  setFullName(v: string): void {
    this.edit({ fullName: v });
  }
  setTitle(v: string): void {
    this.edit({ title: v });
  }
  setPronouns(v: string): void {
    this.edit({ pronouns: v });
  }
  setPhone(v: string): void {
    this.edit({ phone: v });
  }
  setPhoneVisible(v: boolean): void {
    this.edit({ phoneVisible: v });
  }
  setTimezone(zone: string): void {
    this.edit({ timezone: zone });
    this.update({ zoneQuery: "" });
  }
  applyDeviceZone(): void {
    this.setTimezone(this.state.deviceZone);
  }
  setZoneQuery(q: string): void {
    this.update({ zoneQuery: q, zones: this.zoneOptions(q) });
  }

  /** Back to the saved profile. */
  discard(): void {
    const m = this.state.member;
    if (m === undefined) return;
    this.update({ draft: draftFromMember(m, this.state.deviceZone, this.appLocale()), notice: undefined });
    this.revalidate();
  }

  /** Saves the form (optimistic; queued offline). */
  save(): void {
    const s = this.state;
    const m = s.member;
    if (!s.canSave || m === undefined) return;
    const req = profilePatch(draftFromMember(m, s.deviceZone, this.appLocale()), s.draft);
    if (isEmptyPatch(req)) return;
    this.enqueue(req, msg("settings.saved"));
  }

  /** Language applies at once (the screen switches the app's language too). */
  setLocale(locale: Locale): void {
    this.edit({ locale });
    const m = this.state.member;
    if (m === undefined || m.locale === locale) return;
    this.enqueue({ locale });
  }

  // ---------- Status ----------

  setStatusEmoji(emoji: string): void {
    this.update({ statusEmoji: emoji });
  }

  setStatusText(text: string): void {
    const problem = statusProblem(text);
    const next: Partial<ProfileSettingsState> = { statusText: text, statusProblem: problem };
    this.update(next);
  }

  setStatusDuration(d: StatusDuration): void {
    this.update({ statusDuration: d });
  }

  /** Fills the editor with a preset (its translated text). */
  applyPreset(emoji: string, text: string, duration: StatusDuration): void {
    this.update({ statusEmoji: emoji, statusText: text, statusDuration: duration, statusProblem: undefined });
  }

  /** Starts the editor from my current status. */
  editStatus(): void {
    const cur = this.state.currentStatus;
    this.update({ statusEmoji: cur === undefined ? "" : cur.emoji, statusText: cur === undefined ? "" : cur.text, statusProblem: undefined });
  }

  saveStatus(): void {
    const s = this.state;
    if (s.statusProblem !== undefined || (s.statusEmoji === "" && s.statusText.trim() === "")) return;
    const status: UserStatus = { emoji: s.statusEmoji === "" ? "💬" : s.statusEmoji, text: s.statusText.trim() };
    const expiresAt = statusExpiresAt(s.statusDuration, this.now(), this.ws.deps.clock.tzOffsetMinutes(this.now()));
    if (expiresAt !== undefined) status.expiresAt = expiresAt;
    this.enqueue({ status }, msg("profile.status.saved"));
  }

  clearStatus(): void {
    this.update({ statusEmoji: "", statusText: "", statusProblem: undefined });
    this.enqueue({ status: { emoji: "", text: "" } }, msg("profile.status.saved"));
  }

  // ---------- Avatar styles ----------

  /** Opens the picker on the current style (initials on the name's tone by default). */
  openAvatarEditor(defaultTone: number): void {
    const a = this.state.member === undefined ? undefined : memberAvatar(this.state.member);
    const editor: AvatarEditor =
      a === undefined || a.kind === "photo" ? { kind: "initials", color: defaultTone, emoji: "🙂" } : a.kind === "initials" ? { kind: "initials", color: a.color, emoji: "🙂" } : { kind: "emoji", color: a.color, emoji: a.emoji };
    this.update({ avatarEditor: editor });
  }

  setAvatarKind(kind: "initials" | "emoji"): void {
    const e = this.state.avatarEditor;
    if (e !== undefined) this.update({ avatarEditor: { ...e, kind } });
  }

  setAvatarColor(color: number): void {
    const e = this.state.avatarEditor;
    if (e !== undefined && color >= 1 && color <= 8) this.update({ avatarEditor: { ...e, color } });
  }

  setAvatarEmoji(emoji: string): void {
    const e = this.state.avatarEditor;
    if (e !== undefined && emoji !== "") this.update({ avatarEditor: { ...e, kind: "emoji", emoji } });
  }

  closeAvatarEditor(): void {
    this.update({ avatarEditor: undefined });
  }

  /** Saves the picked style (replaces a photo). */
  saveAvatar(): void {
    const e = this.state.avatarEditor;
    if (e === undefined) return;
    const avatar: MemberAvatar = e.kind === "initials" ? { kind: "initials", color: e.color } : { kind: "emoji", emoji: e.emoji, color: e.color };
    this.update({ avatarEditor: undefined });
    this.enqueue({ avatar }, msg("profile.avatar.saved"));
  }

  // ---------- Photo ----------

  pickPhoto(file: PickedFile): void {
    void this.photos.pick(file);
  }

  setPhotoCrop(rect: CropArea): void {
    this.photos.setCrop(rect);
  }

  confirmPhoto(): Promise<boolean> {
    return this.photos.confirm();
  }

  cancelPhoto(): void {
    this.photos.cancel();
  }

  removePhoto(): void {
    const m = this.state.member;
    if (m === undefined || memberAvatar(m) === undefined) return;
    this.enqueue({ removeAvatar: true });
  }

  private async attachPhoto(fileId: string): Promise<void> {
    const me = await this.deps.api.updateProfile(this.wsId, { avatarFileId: fileId }, this.ws.deps.ids.next());
    if (this.isDisposed) return;
    this.adopt(me, !this.state.dirty);
  }

  // ---------- Writes ----------

  private enqueue(req: UpdateProfileRequest, done?: MessageDescriptor): void {
    const m = this.state.member;
    this.queue.push({ req, clientId: this.ws.deps.ids.next() });
    if (m !== undefined) this.update({ member: applyProfilePatch(m, req), draft: req.locale === undefined ? this.state.draft : { ...this.state.draft, locale: req.locale } });
    this.revalidate();
    void this.flush(done);
  }

  private async flush(done?: MessageDescriptor): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    this.update({ saving: true });
    try {
      while (this.queue.length > 0 && !this.isDisposed) {
        const w = this.queue[0]!;
        try {
          const me = await this.deps.api.updateProfile(this.wsId, w.req, w.clientId);
          this.queue.shift();
          if (this.isDisposed) return;
          this.saved = me;
          if (this.queue.length === 0) {
            const resetDraft = isEmptyPatch(profilePatch(draftFromMember(me, this.state.deviceZone, this.appLocale()), this.state.draft)) || !this.state.dirty;
            this.adopt(me, resetDraft);
          }
          this.update({ queued: false });
        } catch (e) {
          if (this.isDisposed) return;
          if (errorKind(e) === "network") {
            this.update({ queued: true, notice: msg("settings.offlineQueued") });
            return;
          }
          // Refused for good: drop it and show the server's profile again.
          const refused = this.queue.shift();
          if ((e as { code?: string }).code === "handle_taken" && refused?.req.handle !== undefined) {
            this.takenHandles.add(refused.req.handle);
            const base = this.saved;
            if (base !== undefined) this.adopt(base, false);
            this.update({ handleStatus: "taken", notice: handleProblemMessage("taken") });
            return;
          }
          const base = this.saved;
          if (base !== undefined) this.adopt(base, false);
          this.update({ notice: msg("settings.failed") });
          return;
        }
      }
      if (done !== undefined && !this.isDisposed) this.update({ notice: done });
    } finally {
      this.flushing = false;
      if (!this.isDisposed) this.update({ saving: false });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
